import axios from 'axios';
import {
  getToken,
  saveToken,
  destroyToken,
  saveAccounts,
  getAccounts,
  setSaveUser,
} from './persistent';
import { set } from 'vue-gtag';

import {
  STORE_ACCESS_TOKEN,
  STORE_REFRESH_TOKEN,
  STORE_USER_DATA,
  STORE_ACCOUNTS,
  PURGE_AUTH,
  LOGIN,
  LOGIN_WITH_PASSWORD,
  GENERATE_ACCESS_TOKEN,
  FETCH_USER_INFO,
} from './types';
import { _partnerExperienceLevel, getEnv } from '../../utils';
import { ContactProfileEnum, UserRoleEnum } from '@gid/models';

export default {
  state: {
    refresh_token: null,
    access_token: null,
    user: null,
    accounts: null,
  },
  getters: {
    isAuthenticated(state) {
      return state.access_token !== null;
    },
    environment() {
      if (getEnv('NODE_ENV') == 'production') {
        return getEnv('GID_ENV') ?? getEnv('VUE_APP_ENV') ?? getEnv('NODE_ENV');
      }
      return getEnv('NODE_ENV');
    },
    locale(state) {
      const urlLocale = new URLSearchParams(window?.location?.href).get('lang');

      if (state.user && state.user.language) {
        return state.user.language.split('_')[0];
      } else if (urlLocale) {
        return urlLocale;
      } else if (navigator.language) {
        return navigator.language.split('-')[0];
      } else {
        return 'de';
      }
    },
    contactProfiles(state) {
      return state.user?.contact.profiles ?? [];
    },
    user(state) {
      if (state.user) {
        return state.user;
      }
      return null;
    },
    useExternalNames(state) {
      if (state.user) {
        return state.user.role === UserRoleEnum.ADMIN ? false : true;
      }
      return true;
    },
    userEditableForRoles(state) {
      if (state.user) {
        switch (state.user.role) {
          case UserRoleEnum.ADMIN:
            return (state.user.contact.profiles || []).filter((profile) =>
              [ContactProfileEnum.GID, ContactProfileEnum.FINANCE].includes(
                profile,
              ),
            );
          case UserRoleEnum.PARTNER:
            return ['Partner'];
          case UserRoleEnum.BRAND:
            return ['Brand'];
          case UserRoleEnum.CUSTOMER:
            return ['Kunde'];
          default:
            return [];
        }
      }
    },
    access_token(state) {
      return state.access_token;
    },
    save_user(state) {
      return state.save_user;
    },
  },
  mutations: {
    [STORE_REFRESH_TOKEN](state, { refresh_token }) {
      state.refresh_token = refresh_token;
    },
    [STORE_ACCESS_TOKEN](state, { access_token }) {
      state.access_token = access_token;
      saveToken(access_token);
      setAuthHeaders(access_token);
    },
    [STORE_ACCOUNTS](state, { accounts }) {
      state.accounts = accounts;
      saveAccounts(accounts);
    },
    [PURGE_AUTH](state) {
      state.refresh_token = null;
      state.access_token = null;
      state.user = null;
      destroyToken();
      resetAuthHeaders();
    },
    [STORE_USER_DATA](state, { user }) {
      state.user = user;
    },
  },
  actions: {
    [LOGIN](_store, loginDetails) {
      return axios.post('/api/auth/app-login', loginDetails);
    },
    [GENERATE_ACCESS_TOKEN](store, { refresh_token, readonly }) {
      store.commit(STORE_REFRESH_TOKEN, { refresh_token });
      return axios({
        url: '/api/auth/access-token',
        method: 'post',
        headers: { Authorization: `Bearer ${store.state.refresh_token}` },
        data: { readonly },
      }).then((response) => {
        store.commit(STORE_ACCESS_TOKEN, {
          access_token: response.data.access_token,
        });
      });
    },
    [LOGIN_WITH_PASSWORD](store, loginDetails) {
      const { saveUser, ...rest } = loginDetails;
      setSaveUser(saveUser);

      return axios({
        url: '/api/auth/app-login',
        method: 'post',
        data: rest,
      }).then((response) => {
        const accounts = response.data;

        if (accounts && accounts.length > 0) {
          store.commit(STORE_ACCOUNTS, {
            accounts: accounts,
          });
        }
        store.commit(STORE_ACCESS_TOKEN, {
          access_token: accounts[0].access_token,
        });
      });
    },
    [FETCH_USER_INFO](store) {
      return axios.get('/api/auth/me').then((response) => {
        const user = response.data;

        store.commit(STORE_USER_DATA, {
          user,
        });

        if (user) {
          let userProperties = {};

          if (user.role === UserRoleEnum.BRAND) {
            userProperties = {
              brand_name: user.account.name,
              brand_id: user.account.id,
            };
          }
          if (user.role === UserRoleEnum.PARTNER) {
            userProperties = {
              partner_id: user.account.id,
              partner_name: user.account.name,
            };
          }

          set('user_properties', userProperties);
        }
      });
    },
    [STORE_ACCESS_TOKEN](store, access_token) {
      store.commit(STORE_ACCESS_TOKEN, {
        access_token: access_token,
      });
    },
  },
};

function setAuthHeaders(token) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

function resetAuthHeaders() {
  delete axios.defaults.headers.common['Authorization'];
}

export function initAuth(store) {
  // Watch for having gained access to fetch user info
  store.subscribe(({ type }) => {
    if (type === STORE_ACCESS_TOKEN) {
      store.dispatch(FETCH_USER_INFO);
    }
  });

  // Watch for token expiration
  axios.interceptors.response.use(null, function (error) {
    const { url, method, data, headers } = error.config || {};

    // Most probably the token has expired
    if (
      error.response &&
      error.response.status === 401 &&
      url?.startsWith('/api/auth/')
    ) {
      console.error('Logging out due to 401 on auth !');
      store.commit(PURGE_AUTH);
    }
    // Handle 504 Gateway Timeout errors for auth requests
    if (
      error.response &&
      error.response.status === 504 &&
      url?.startsWith('/api/auth/')
    ) {
      console.warn(
        `Received 504 for auth request to ${url}, will retry in 60 seconds`,
      );

      // Create a retryable request
      const retryRequest = () => {
        console.log(`Retrying auth request to ${url}`);
        return axios({
          url,
          method,
          data,
          headers,
        });
      };

      // Retry after 60 seconds
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(retryRequest());
        }, 60000);
      });
    }
    return Promise.reject(error);
  });

  // Trigger authenticated state if having access_token from previous visit
  const access_token = getToken();
  if (access_token) {
    store.commit(STORE_ACCESS_TOKEN, {
      access_token: access_token,
    });
  }
  const accounts = getAccounts();
  if (accounts) {
    store.commit(STORE_ACCOUNTS, {
      accounts: accounts,
    });
  }
}

export function checkRouteAccess({ route, user, isAuthenticated }) {
  const needsAuth = route.meta?.auth;

  if (needsAuth) {
    // Route requires authentication
    if (!isAuthenticated) {
      return false;
    }
    // If access can't be determined yet, because user data is not loaded
    if (!user) {
      return null;
    }

    return (
      checkAuthContactProfiles(route, user) &&
      checkAuthAccountProfiles(route, user) &&
      checkAuthAccountStatus(route, user)
    );
  }

  return true;
}

function checkAuthContactProfiles(route, user) {
  if (route.meta.auth.contactProfiles) {
    const contactProfiles = user.contact?.profiles || [];

    if (route.meta.auth.contactProfiles === true) {
      // Any logged user can access
      return true;
    } else if (route.meta.auth.contactProfiles instanceof Array) {
      // Only users with matching contact profiles can access
      return Array.isArray(contactProfiles)
        ? route.meta.auth.contactProfiles.some((p) =>
            contactProfiles.includes(p),
          )
        : false;
    } else if (typeof route.meta.auth.contactProfiles === 'function') {
      return route.meta.auth.contactProfiles(route, contactProfiles);
    }
    return false;
  } else {
    // Route doesn't require authentication
    return true;
  }
}

function checkAuthAccountProfiles(route, user) {
  if (route.meta.auth.accountProfiles) {
    const accountProfiles = user.account.profiles || [];

    if (route.meta.auth.accountProfiles instanceof Array) {
      // Only users with matching account profiles can access
      return Array.isArray(accountProfiles)
        ? route.meta.auth.accountProfiles.some((p) =>
            accountProfiles.includes(p),
          )
        : false;
    } else if (typeof route.meta.auth.accountProfiles === 'function') {
      return route.meta.auth.accountProfiles(route, accountProfiles);
    }
    return false;
  } else {
    // Route doesn't require specific account profiles
    return true;
  }
}

export function checkAuthAccountStatus(route, user) {
  if (Array.isArray(route.meta.auth.accountStatuses)) {
    return route.meta.auth.accountStatuses.includes(
      user?.account?.account_status,
    );
  }
  if (route.meta.auth.accountStatuses == user?.account?.account_status) {
    return true;
  }
  if (!route.meta.auth.accountStatuses) {
    return true;
  }
  return false;
}
